import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { getApiAsync } from "../../../../api";
import { BotError } from "../../../../api/graphql";

type BotErrorPanelProps = { parentId: string };

export const BotErrorPanel: React.FC<BotErrorPanelProps> = ({ parentId }) => {
  const rootStore = useRootStore();
  // Extraídos del rootStore para que ESLint vea la cadena de deps del useEffect.
  const { langStore, authStore, activityStore } = rootStore;
  const [items, setItems] = useState<BotError[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        if (cancelled) return;
        const api = await getApiAsync(authStore, activityStore);
        const result = await api.botErrorListWithOffset({
          where: { parent_BotRun_errors: { id: { equals: parentId } } },

          take: 100,
          offset: 0,
        });
        if (!cancelled) {
          setItems(result.botErrorListWithOffset as BotError[]);
        }
      } catch (error) {
        console.error("Failed to load botError:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [parentId, authStore, activityStore]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        {langStore.BotErrorLabels?.p || "BotError"}
        {loading && <CircularProgress size={16} />}
        {!loading && items.length === 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({langStore.noRecordsMessage})
          </Typography>
        )}
      </Typography>
      {!loading && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {langStore.BotErrorErrorTypeLabel || "errorType"}
              </TableCell>
              <TableCell>
                {langStore.BotErrorSeverityLabel || "severity"}
              </TableCell>
              <TableCell>
                {langStore.BotErrorMessageLabel || "message"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.openDrawerView("botError", item.id);
                  }}
                >
                  <TableCell>{String(item.errorType ?? "-")}</TableCell>
                  <TableCell>{String(item.severity ?? "-")}</TableCell>
                  <TableCell>{String(item.message ?? "-")}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
